import { useEffect } from "react";
import { useSessionMonitor } from "@/hooks/useSessionMonitor";
import { useSuspiciousActivityDetector } from "@/hooks/useSuspiciousActivityDetector";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface SessionMonitorProps {
  isAdminRoute?: boolean;
}

export function SessionMonitor({ isAdminRoute = false }: SessionMonitorProps) {
  const { showWarning, updateActivity } = useSessionMonitor(isAdminRoute);
  const { user } = useAuth();
  const navigate = useNavigate();
  useSuspiciousActivityDetector(user);

  useEffect(() => {
    // Listen for role revocations and force logout
    const setupRoleRevocationListener = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const channel = supabase
        .channel('role-revocation-monitor')
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'user_roles',
            filter: `user_id=eq.${user.id}`
          },
          async () => {
            // Role was revoked - force logout
            await supabase.auth.signOut();
            navigate('/auth');
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    setupRoleRevocationListener();
  }, [navigate]);

  return (
    <AlertDialog open={showWarning}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Session Expiring Soon</AlertDialogTitle>
          <AlertDialogDescription>
            Your session will expire in 5 minutes due to inactivity. 
            Click below to continue your session.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={updateActivity}>
            Continue Session
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
